import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import {
  Container,
  Grid,
  Segment,
  Header,
  Icon,
  Message,
  Input,
  Dropdown
} from 'semantic-ui-react';
import { Button, Form } from 'formik-semantic-ui';
import { UnsavedFormPrompt } from '../../_components/custom/UnsavedFormPropt';
import { setToast } from '../../store/ToastManagement';
import {
  servicesAPI,
  useCurrentUser,
  useCustomer,
  useExternalAccounts,
  useCustomerVehicles,
  useReferenceVehicles,
  useLanceToCustomers
} from '../../_services';
import { isAON, hubCustomerOnboarded } from '../../utils';
import {
  CustomerContact,
  Credentials,
  CardDetails,
  DomesticTariffDetails
} from '../../_components/users/';
import './customerDetail_styles.css';

export const CustomerProfile = ({ id = null }) => {
  const { currentUser } = useCurrentUser();
  const { customer, setCustomer } = useCustomer(id);
  const { customerVehicles } = useCustomerVehicles(id);
  const referenceVehicles = useReferenceVehicles();
  const { externalAccounts, setExternalAccounts } = useExternalAccounts(id);
  const lanceToCustomers = useLanceToCustomers(id);

  const [cbTouched, setCBTouched] = useState(false);

  const [init, setInit] = useState(true);
  const [externalCompanyId, setExternalCompanyId] = useState('');
  const [externalCompanyAccountNo, setExternalCompanyAccountNo] = useState('');
  const [editVehicles, setEditVehicles] = useState(null);
  const [selectedVehicle, setSelectedVehicle] = useState(0);
  const [editMake, setEditMake] = useState(null);
  const [editModel, setEditModel] = useState(null);
  const [editVariant, setEditVariant] = useState(null);
  const [editRegistration, setEditRegistration] = useState('');

  // TDTODO replace with new T&Cs when ready
  const isPaulBragg =
    customer?.id === 'CCCR056d81a0-02cb-405c-a34d-22f622158ecd';
  const paysByCard = !isPaulBragg;

  if (currentUser) {
    // Ensure they've signed the T&Cs
    // if (customer && customer.agreed_to_terms_datetime === '') {
    //   document.location.href = '/customer/terms';
    // }
  }

  useEffect(() => {
    if (externalAccounts && externalAccounts.length > 0) {
      setExternalCompanyId(externalAccounts[0].external_company_id);
      setExternalCompanyAccountNo(externalAccounts[0].external_account_number);
    }
  }, [externalAccounts]);

  useEffect(() => {
    if (customerVehicles && referenceVehicles.length) {
      setEditVehicles(
        customerVehicles.concat([
          {
            id: '',
            vehicle_id: '',
            registration_no: ''
          }
        ])
      );
    }
  }, [customerVehicles, referenceVehicles]);

  const selectVehicle = idx => {
    if (!referenceVehicles.length || !editVehicles) return;

    const rvs = referenceVehicles.filter(
      rv => rv.id === editVehicles[idx].vehicle_id
    );
    if (rvs.length === 1) {
      setEditMake(rvs[0].make);
      setEditModel(rvs[0].model);
      setEditVariant(rvs[0].id);
    } else {
      setEditMake('');
      setEditModel('');
      setEditVariant('');
    }
    setEditRegistration(editVehicles[idx].registration_no);
  };

  useEffect(() => {
    if (init && referenceVehicles.length && editVehicles) {
      setInit(false);
      selectVehicle(selectedVehicle);
    }
  }, [init, editVehicles, selectedVehicle, referenceVehicles]); // eslint-disable-line

  const dispatch = useDispatch();
  const displaySuccessMessage = () =>
    dispatch(
      setToast({
        title: 'Success!',
        body: 'Account changes were successfully saved.'
      })
    );

  if (!customer || !customerVehicles || !externalAccounts) {
    return <div>Loading</div>;
  }

  const vehicleOptions = customerVehicles
    .map((v, idx) => {
      let text = v.registration_no;
      if (referenceVehicles) {
        const rvs = referenceVehicles.filter(rv => rv.id === v.vehicle_id);
        if (rvs.length === 1) {
          text = `${v.registration_no} ${rvs[0].make} ${rvs[0].model} ${rvs[0].variant}`;
        }
      }
      return {
        text: text,
        value: idx
      };
    })
    .concat([
      {
        text: 'Register new vehicle',
        value: customerVehicles.length
      }
    ]);

  const makeOptions = referenceVehicles
    .map(v => v.make)
    .filter((v, idx, self) => self.indexOf(v) === idx)
    .map(v => {
      return {
        text: v,
        value: v
      };
    });

  const modelOptions = editMake
    ? referenceVehicles
        .filter(v => v.make === editMake)
        .map(v => v.model)
        .filter((v, idx, self) => self.indexOf(v) === idx)
        .map(v => {
          return {
            text: v,
            value: v
          };
        })
    : [];

  const variantOptions =
    editMake && editModel
      ? referenceVehicles
          .filter(v => v.make === editMake && v.model === editModel)
          .filter((v, idx, self) => self.indexOf(v) === idx)
          .map(v => {
            return {
              text: v.variant,
              value: v.id
            };
          })
      : [];

  const activateLances = customerId => {
    lanceToCustomers.forEach(ltc =>
      servicesAPI.InventoryServices('activateLance', ltc.lance_tag_id).then(
        response => {},
        error => {}
      )
    );
  };

  const handleSubmit = (values, formikApi) => {
    if (!hubCustomerOnboarded(values)) {
      formikApi.setSubmitting(false);
      formikApi.setStatus({ error: 'Please complete fields marked *' });
      return;
    }

    values = {
      ...values,
      id
    };

    servicesAPI.CustomerServices('update', values, id).then(
      response => {
        setCustomer(values);

        // Update cache
        localStorage.setItem('customerOnboarded', true);

        values = {
          external_company_id: externalCompanyId,
          external_account_number: externalCompanyAccountNo,
          account_purpose: ''
        };

        servicesAPI.CustomerServices('updateExternalAccounts', values, id).then(
          response => {
            setExternalAccounts(values);

            values = editVehicles;

            servicesAPI.CustomerServices('updateVehicles', values, id).then(
              response => {
                // Activate Lances if onboarded
                if (
                  localStorage.getItem('customerOnboarded') === 'true' &&
                  localStorage.getItem('accountOnboarded') === 'true'
                )
                  activateLances(id);

                displaySuccessMessage();

                setCBTouched(false);
                formikApi.resetForm();
              },
              error => {
                formikApi.setSubmitting(false);
                formikApi.setStatus({ error: error });
              }
            );
          },
          error => {
            formikApi.setSubmitting(false);
            formikApi.setStatus({ error: error });
          }
        );
      },
      error => {
        formikApi.setSubmitting(false);
        formikApi.setStatus({ error: error });
      }
    );
  };

  return (
    <div>
      <Container>
        <Grid centered padded>
          <Grid.Column style={{ maxWidth: 500 }}>
            <Form
              ignoreLoading
              onSubmit={handleSubmit}
              initialValues={customer}
              enableReinitialize={true}
            >
              {({
                values,
                errors,
                status,
                touched,
                isValid,
                isSubmitting,
                handleChange,
                setFieldValue,
                values: customer
              }) => (
                <div
                  style={{
                    marginBottom: '0px',
                    minHeight: '660px',
                    paddingLeft: '0px',
                    paddingRight: '0px',
                    paddingBottom: '0px'
                  }}
                >
                  <Segment>
                    <UnsavedFormPrompt
                      when={!!Object.keys(touched).length || cbTouched}
                    />
                    <Grid columns={1}>
                      <Grid.Row>
                        <Grid.Column textAlign="center">
                          <Header as="h2">PROFILE</Header>
                        </Grid.Column>
                      </Grid.Row>
                      <Grid.Row>
                        <Grid.Column textAlign="center">
                          <Message visible>
                            Please note fields with a * are mandatory
                          </Message>
                        </Grid.Column>
                      </Grid.Row>
                      {!!Object.keys(errors).length && (
                        <Grid.Column width={16}>
                          <Message negative visible>
                            Please correct requirements in the form
                          </Message>
                        </Grid.Column>
                      )}
                    </Grid>
                    <Grid columns={1} divided stackable>
                      <Grid.Row>
                        <Grid.Column>
                          <Form.Children>
                            <CustomerContact
                              handleSubmit={handleSubmit}
                              customer={customer}
                              setFieldValue={setFieldValue}
                              redactedForGDPR={false}
                              copyDetails={false}
                            />
                          </Form.Children>
                        </Grid.Column>
                      </Grid.Row>
                    </Grid>
                  </Segment>
                  {/*<Segment>
                    <Grid columns={1} divided stackable>
                      <Grid.Row>
                        <Grid.Column>
                          <Header as="h3">
                            <Icon name="address card" />
                            External Account
                          </Header>
                          <Form.Children>
                            <p
                              sub
                              color="grey"
                              style={{ marginTop: '15px', marginBottom: '5px' }}
                            >
                              <b>Company</b>
                            </p>
                            <Input
                              type="text"
                              inputProps={{
                                placeholder: 'Company'
                              }}
                              name="externalCompanyId"
                              value={externalCompanyId}
                              onChange={(_, { value }) => {
                                setExternalCompanyId(value);
                                setCBTouched(true);
                              }}
                            />
                            <p
                              sub
                              color="grey"
                              style={{ marginTop: '15px', marginBottom: '5px' }}
                            >
                              <b>Account number</b>
                            </p>
                            <Input
                              type="text"
                              inputProps={{
                                placeholder: 'Account number'
                              }}
                              name="externalCompanyAccountNo"
                              value={externalCompanyAccountNo}
                              onChange={(_, { value }) => {
                                setExternalCompanyAccountNo(value);
                                setCBTouched(true);
                              }}
                            />
                          </Form.Children>
                        </Grid.Column>
                      </Grid.Row>
                    </Grid>
                  </Segment>*/}
                  {paysByCard && (
                    <Segment>
                      <Grid columns={1} divided stackable>
                        <Grid.Row>
                          <Grid.Column>
                            <CardDetails customerId={id} edit={true} />
                          </Grid.Column>
                        </Grid.Row>
                      </Grid>
                    </Segment>
                  )}
                  {isAON(customer) ? (
                    <Segment>
                      <Grid columns={1} divided stackable>
                        <Grid.Row>
                          <Grid.Column>
                            <DomesticTariffDetails customerId={id} />
                          </Grid.Column>
                        </Grid.Row>
                      </Grid>
                    </Segment>
                  ) : (
                    ''
                  )}
                  <Segment>
                    <Grid columns={1} divided stackable>
                      <Grid.Row>
                        <Grid.Column>
                          <Header as="h3">
                            <Icon name="car" />
                            Vehicle Details
                          </Header>
                          <Form.Children>
                            <p
                              sub
                              color="grey"
                              style={{ marginTop: '15px', marginBottom: '5px' }}
                            >
                              <b>Select Vehicle</b>
                            </p>
                            <Dropdown
                              selection
                              search
                              options={vehicleOptions}
                              onChange={(_, { value }) => {
                                setCBTouched(true);
                                setSelectedVehicle(value);
                                selectVehicle(value);
                              }}
                              value={selectedVehicle}
                            />
                            <p
                              sub
                              color="grey"
                              style={{ marginTop: '15px', marginBottom: '5px' }}
                            >
                              <b>Make</b>
                            </p>
                            <Dropdown
                              placeholder="Make"
                              selection
                              search
                              options={makeOptions}
                              onChange={(_, { value }) => {
                                setCBTouched(true);
                                setEditMake(value);
                              }}
                              value={editMake}
                            />
                            <p
                              sub
                              color="grey"
                              style={{ marginTop: '15px', marginBottom: '5px' }}
                            >
                              <b>Model</b>
                            </p>
                            <Dropdown
                              placeholder="Model"
                              selection
                              search
                              options={modelOptions}
                              onChange={(_, { value }) => {
                                setCBTouched(true);
                                setEditModel(value);
                              }}
                              value={editModel}
                            />
                            <p
                              sub
                              color="grey"
                              style={{ marginTop: '15px', marginBottom: '5px' }}
                            >
                              <b>Variant</b>
                            </p>
                            <Dropdown
                              placeholder="Variant"
                              selection
                              search
                              options={variantOptions}
                              onChange={(_, { value }) => {
                                setCBTouched(true);
                                setEditVariant(value);
                                editVehicles[
                                  selectedVehicle
                                ].vehicle_id = value;
                              }}
                              value={editVariant}
                            />
                            <p
                              sub
                              color="grey"
                              style={{ marginTop: '15px', marginBottom: '5px' }}
                            >
                              <b>Registration</b>
                            </p>
                            <Input
                              type="text"
                              inputProps={{
                                placeholder: 'Vehicle Registration'
                              }}
                              name="editRegistration"
                              onChange={(_, { value }) => {
                                setCBTouched(true);
                                setEditRegistration(value);
                                editVehicles[
                                  selectedVehicle
                                ].registration_no = value;
                              }}
                              value={editRegistration}
                            />
                          </Form.Children>
                        </Grid.Column>
                      </Grid.Row>
                    </Grid>
                  </Segment>
                  <Segment>
                    <Grid columns={1} divided stackable>
                      <Grid.Row>
                        <Grid.Column>
                          <Credentials />
                        </Grid.Column>
                      </Grid.Row>
                    </Grid>
                  </Segment>
                  <Button.Submit
                    style={{
                      width: '100%'
                    }}
                    primary
                    type="submit"
                    disabled={
                      // !isValid ||
                      // !accountOnboarded ||
                      isSubmitting ||
                      (!Object.keys(touched).length && !cbTouched)
                    }
                    loading={isSubmitting}
                    color="olive"
                  >
                    SAVE DETAILS
                  </Button.Submit>
                </div>
              )}
            </Form>
          </Grid.Column>
        </Grid>
      </Container>
    </div>
  );
};
